import {Swiper, SwiperOptions, Pagination, Navigation} from "swiper";
import {throttleEvent} from "../module/throttle-event.function";
import {viewportIsMobile} from "../module/match-media.function";
import {PaginationOptions} from "swiper/types";
import {getElementIndex} from "../module/get-element-index.function";
import {teamConfig} from "./config/team.config";

export class SwiperControllerClass {
    readonly customConfig: Record<string, SwiperOptions> = {
        team: { ... teamConfig },
    }
    readonly genericSwiperSelector = '.js-swiper';
    readonly genericDefaultConfig: SwiperOptions = {
        modules: [Pagination, Navigation],
        pagination: { el: '.swiper-pagination', clickable: true },
        navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
        },
        simulateTouch: false,
        keyboard: {
            enabled: true,
            onlyInViewport: false,
        },
        autoHeight: true,
        on: {
            slideChangeTransitionEnd: function (swiper) {
                swiper.updateAutoHeight(200);
            },
            init: function(swiper) {
                swiper.updateAutoHeight(200);
            }
        }
    }

    private genericSwiperRefs: {[selector: string]: Swiper} = {}
    constructor() {
        if (SwiperControllerClass.swiperExists(this.genericSwiperSelector)) {
            this.initGeneric();
        }
    }

    private initGeneric() {
        Swiper.use([Pagination, Navigation])
        const elems = Array.from(document.querySelectorAll<HTMLElement>(this.genericSwiperSelector));

        elems.forEach(
            (elem, index) => {
                const identifierSelector = `${this.genericSwiperSelector}-${index}`;
                elem.classList.add(identifierSelector.slice(1));

                if (SwiperControllerClass.isMobileOnly(elem) && !viewportIsMobile()) {
                    return;
                }

                if (elem.getAttribute('data-swiper-shuffle') === 'y') {
                    this.shuffle(elem);
                }

                this.genericSwiperRefs[identifierSelector] = new Swiper(
                    identifierSelector,
                    this.getConfig(elem),
                )
            });

        if (Object.keys(this.genericSwiperRefs).length > 0) {
            this.listenViewportResize();
        }
    }

    private getConfig(elem: HTMLElement): SwiperOptions {
        let config = {  ... this.genericDefaultConfig }

        if (elem.getAttribute('data-swiper-pagination-type')) {
            (config.pagination as PaginationOptions).type = elem.getAttribute('data-swiper-pagination-type') as undefined as any;
        }

        if (elem.getAttribute('data-swiper-detect-initial-slide') === 'y') {
            const initialElem = elem.querySelector<HTMLElement>('.swiper-initial-slide');
            config.initialSlide = initialElem ? getElementIndex(initialElem) : undefined;
        }

        if (elem.getAttribute('data-swiper-slides-per-view')) {
            config.slidesPerView = parseInt(elem.getAttribute('data-swiper-slides-per-view'));
        }

        if (elem.getAttribute('data-swiper-centered-slides')) {
            config.centeredSlides = true;
        }

        if (elem.getAttribute('data-swiper-auto-height-false')) {
            config.autoHeight = false;
        }

        if (elem.getAttribute('data-swiper-config')) {
            config = { ... config, ... this.customConfig?.[elem.getAttribute('data-swiper-config')]};
        }

        config.initialSlide = this.getInitialSlideFromHash(elem) || 0;

        if (elem.getAttribute('data-swiper-breakpoints')) {
            config.breakpoints = JSON.parse(elem.getAttribute('data-swiper-breakpoints'));
        }

        return config;
    }

    private static swiperExists(selector: string): boolean {
        return !!document.querySelector(selector);
    }

    private static isMobileOnly(elem: HTMLElement): boolean {
        return elem.getAttribute('data-swiper-breakpoint') === 'xs';
    }

    private onViewportResize(): void {
        const swiperClasses = Object.keys(this.genericSwiperRefs);
        swiperClasses.forEach(swiperClass => {
            const swiperElem = document.querySelector<HTMLElement>(swiperClass);

            if (SwiperControllerClass.isMobileOnly(swiperElem) && !viewportIsMobile()) {
                this.genericSwiperRefs[swiperClass].destroy(true, true);
            }
        });
    }

    private listenViewportResize(): void {
        window.addEventListener('resize', throttleEvent(() => {
            this.onViewportResize();
        }, 500));
    }

    getSwiperInstance(index: number): Swiper {
        return this.genericSwiperRefs[`${this.genericSwiperSelector}-${index}`];
    }

    /**
     * Shuffle elements
     * @param elem
     * @private
     */
    private shuffle(elem: HTMLElement) {
        const slides = elem.querySelector('.swiper-wrapper');
        for (let i = slides.children.length; i >= 0; i--) {
            slides.appendChild(slides.children[Math.random() * i | 0]);
        }
    }

    /**
     * Calculate the slide index matching the location hash
     * @param elem
     * @private
     */
    private getInitialSlideFromHash(elem: HTMLElement) {
        if (!location.hash) {
            return 0;
        }

        const slideElem = elem.querySelector(`${location.hash}.swiper-slide`);

        if (!slideElem) {
            return 0;
        }

        // scrollIntoView with scroll-margin-top not working on overflow element
        elem.parentElement.scrollIntoView();
        return [ ... slideElem.parentElement.children ].indexOf(slideElem);
    }
}
